import type RouterService from '@ember/routing/router-service';

type NativeMessageHandlerName = 'navigation' | 'theme';

interface NavigationMessage {
	navigation: { hide: true } | { show: true };
}

interface ThemeMessage {
	theme: { background: string };
}

function sendNativeMessage(
	handlerName: NativeMessageHandlerName,
	message: NavigationMessage | ThemeMessage,
): void {
	// iOS (WKWebView) — uses named message handler
	const handler = window.webkit?.messageHandlers?.[handlerName];
	if (handler) {
		handler.postMessage(message);
		return;
	}

	// Android (WebView) — uses postMessage to parent
	// Wildcard origin is safe here: Android WebView has no real cross-origin context,
	// and the message is only consumed by the native app shell.
	window.parent.postMessage(message, '*');
}

// Lets the native app colour the controls it draws over the board page.
export function sendNativeBoardBackground(background: string): void {
	sendNativeMessage('theme', { theme: { background } });
}

// Pages and popups each hide the navigation for as long as they're shown, so it only comes back once none of them does.
let nativeNavigationHideCount = 0;

export function hideNativeNavigation(): void {
	nativeNavigationHideCount++;
	sendNativeMessage('navigation', { navigation: { hide: true } });
}

export function showNativeNavigation(): void {
	nativeNavigationHideCount = Math.max(0, nativeNavigationHideCount - 1);
	if (nativeNavigationHideCount === 0) {
		sendNativeMessage('navigation', { navigation: { show: true } });
	}
}

export function registerNavigateTo(router: RouterService): void {
	window.navigateTo = (url: string) => {
		router.transitionTo(url);
	};
}

export function unregisterNavigateTo(): void {
	delete window.navigateTo;
}
